import React, { useState } from "react";
import api from "../services/api";
import { STATUS_OPTIONS, ADMIN_TRANSFER_OPTIONS } from "./statusUpdateConstants";

const TRIM_FIELDS = [
  { key: "permission_status", label: "Permission Status", type: "status" },
  { key: "final_amount", label: "Final Amount (₹)", type: "number" },
  { key: "admin_transfer", label: "Admin Transfer", type: "admin_transfer" },
  { key: "statusbygardenassistant", label: "Status by Garden Assistant", type: "status" },
  { key: "statusbygardensuperintendent", label: "Status by Garden Superintendent", type: "status" },
  { key: "statusbytreeofficer", label: "Status by Tree Officer", type: "status" },
  { key: "statusbydycommissioner", label: "Status by Dy. Commissioner", type: "status" },
];

const TrimmingStatusUpdate = () => {
  const [permissionId, setPermissionId] = useState("");
  const [searchId, setSearchId] = useState("");
  const [data, setData] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSearch = async (e) => {
    e?.preventDefault();
    const id = (searchId || permissionId).trim();
    if (!id) {
      setError("Please enter Permission ID");
      return;
    }
    setError("");
    setSuccess("");
    setLoading(true);
    setData(null);
    try {
      const res = await api.get(`/trimming/trimming/${encodeURIComponent(id)}`);
      const row = res?.data?.data || res?.data;
      if (!row) {
        setError("Application not found.");
        setLoading(false);
        return;
      }
      setPermissionId(row.permission_id || id);
      setData(row);
      const initial = {};
      TRIM_FIELDS.forEach((f) => {
        const v = row[f.key];
        initial[f.key] = v === undefined || v === null ? "" : String(v);
      });
      setFormValues(initial);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load application.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!permissionId) return;
    setError("");
    setSuccess("");
    setUpdating(true);
    try {
      const payload = {};
      TRIM_FIELDS.forEach((f) => {
        const v = formValues[f.key];
        if (f.type === "number") {
          payload[f.key] = v === "" ? null : Number(v);
        } else {
          payload[f.key] = v === "" ? null : v;
        }
      });
      await api.put(`/trimming/trimming/superadmin-status-update/${encodeURIComponent(permissionId)}`, payload);
      setSuccess("Status updated successfully.");
      handleSearch();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Update failed.");
    } finally {
      setUpdating(false);
    }
  };

  const renderField = (field) => {
    const value = formValues[field.key] ?? "";
    if (field.type === "number") {
      return (
        <input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => handleChange(field.key, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      );
    }
    if (field.type === "admin_transfer") {
      return (
        <select
          value={value}
          onChange={(e) => handleChange(field.key, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          {ADMIN_TRANSFER_OPTIONS.map((opt) => (
            <option key={opt || "empty"} value={opt}>{opt || "(None)"}</option>
          ))}
        </select>
      );
    }
    return (
      <select
        value={value}
        onChange={(e) => handleChange(field.key, e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
      >
        <option value="">(Select)</option>
        {STATUS_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  };

  return (
    <div className="p-6 max-w-4xl">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Trimming – Update Status by Permission ID
      </h2>

      <form onSubmit={handleSearch} className="mb-6 flex gap-2 flex-wrap items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Permission ID</label>
          <input
            type="text"
            value={searchId || permissionId}
            onChange={(e) => setSearchId(e.target.value)}
            placeholder="e.g. BELP1912"
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? "Searching..." : "Search"}
        </button>
      </form>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md">
          {success}
        </div>
      )}

      {data && (
        <div className="bg-white border border-gray-200 rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-4">
            Application: <strong>{data.permission_id}</strong>
            {data.first_name != null && (
              <span className="ml-2">
                – {[data.first_name, data.last_name].filter(Boolean).join(" ")}
              </span>
            )}
          </p>
          <form onSubmit={handleUpdate} className="space-y-4">
            {TRIM_FIELDS.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                </label>
                {renderField(field)}
              </div>
            ))}
            <div className="pt-4 flex gap-2">
              <button
                type="submit"
                disabled={updating}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {updating ? "Updating..." : "Update Status"}
              </button>
              <button
                type="button"
                onClick={() => setData(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Clear
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default TrimmingStatusUpdate;
